import type { Dispatch, SetStateAction } from "react";
import React, { useContext, useState } from "react";
import styled, { ThemeContext } from "styled-components";
import { CaretDownIcon } from "../Icons/Icons";
import { H3, SoftHeader2 } from "../Typography/Typography";

interface IStyleAccordionCardProps {
  showContent?: boolean;
  hideHeaderBorder?: boolean;
}

const AccordionCardWrapper = styled.div<IStyleAccordionCardProps>`
  position: relative;
  width: 100%;
  color: #0f1d2b;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: all 0.2s ease-out;
`;

const AccordionCardHeader = styled.div<IStyleAccordionCardProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  cursor: pointer;
  transition: all 0.2s ease-out;
  CaretDownIcon {
    transform: ${({ showContent }) =>
      showContent ? "rotate(0deg)" : "rotate(-90deg)"};
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;
const Header = styled.div<IStyleAccordionCardProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  width: 100%;
  min-width: 0;
`;

const Actions = styled.div<IStyleAccordionCardProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  svg {
    width: 16px;
    height: 16px;
    vertical-align: middle;
  }

  button {
    white-space: nowrap;
    margin-right: 5px;
    padding-right: 10px;
  }
`;
const CaretDown = styled.div<IStyleAccordionCardProps>`
  transform: ${({ showContent }) =>
    showContent ? "rotate(0deg)" : "rotate(-90deg)"};
  transition: all 0.2s ease-out;
  height: 16px;
`;
export const Content = styled.div<IStyleAccordionCardProps>`
  width: 100%;
  min-width: 180px;
  padding: ${({ theme }) => theme.resolve("spacing_lg")} 0
    ${({ theme }) => theme.resolve("spacing_lg")};
  transition: all 0.2s ease-out;
  height: ${({ showContent }) => (showContent ? "auto" : "0")};
  margin-top: ${({ hideHeaderBorder }) =>
    hideHeaderBorder ? 0 : ({ theme }) => theme.resolve("spacing_lg")};
  border-top: ${({ theme, hideHeaderBorder }) =>
    hideHeaderBorder
      ? "0px"
      : `${theme.resolve("border_md")} solid ${theme.secondaryBorder}`};
  .ql-editor {
    padding: 0;
    font-size: ${({ theme }) => theme.fontSizes.medium};
  }

  .ql-container .ql-editor {
    padding: 13px;
  }
`;

export interface IAccordionCardProps {
  children: React.ReactNode;
  actionChildren: (
    setShowContent: Dispatch<SetStateAction<boolean | undefined>>
  ) => JSX.Element;
  title: string | (() => JSX.Element);
  subtitle?: string;
  expanded?: boolean;
  onToggle?: (isExpanded: boolean) => void;
  hideHeaderBorder?: boolean;
}

/**
 * edit button will not display if handleEdit callback is not passed in.
 * cancel button will not appear if handleCancel is not passed in.
 */
export const AccordionCard = ({
  children,
  actionChildren,
  title,
  subtitle,
  expanded,
  onToggle,
  hideHeaderBorder = false,
}: IAccordionCardProps) => {
  const [showContent, setShowContent] = useState(expanded);
  const theme = useContext(ThemeContext);
  const handleToggle = () => {
    const next = !showContent;
    setShowContent(next);
    onToggle?.(next);
  };

  return (
    <AccordionCardWrapper showContent={showContent}>
      <AccordionCardHeader showContent={showContent}>
        <Header>
          <CaretDown showContent={showContent} onClick={handleToggle}>
            <CaretDownIcon
              height={16}
              width={16}
              fill={theme.secondaryTextColor}
            />
          </CaretDown>
          <TitleContainer onClick={handleToggle}>
            {typeof title === "string" ? (
              <H3 style={{ margin: 0 }}>{title}</H3>
            ) : (
              title()
            )}
            {subtitle && <SoftHeader2>{subtitle}</SoftHeader2>}
          </TitleContainer>
        </Header>
        <Actions showContent={showContent}>
          {actionChildren(setShowContent)}
        </Actions>
      </AccordionCardHeader>

      {showContent && (
        <Content
          className="accordion-content-wrapper"
          showContent={showContent}
          hideHeaderBorder={hideHeaderBorder}
        >
          {children}
        </Content>
      )}
    </AccordionCardWrapper>
  );
};
